<template>
  <modal
    :show="show"
    @close="close()"
  >
    <form
      v-if="member !== null"
      @submit.prevent="editTeamMember"
    >
      <div class="w-full h-full p-6 space-y-6">
        <div class="w-full flex justify-between">
          <h2 class="text-xl">
            Edit Team Member
          </h2>

          <span
            class="text-gray-300 hover:text-gray-900 cursor-pointer"
            @click="close()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </span>
        </div>
        <!-- Member Info -->
        <div class="col-span-6 sm:col-span-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="flex-shrink-0 h-14 w-14">
                <img
                  class="h-14 w-14 rounded-full object-cover"
                  :src="member.profile_photo_url"
                  alt=""
                >
              </div>
              <div class="ml-4">
                <div class="text-md font-medium text-gray-900">
                  {{ member.name }}
                </div>
                <div class="text-sm text-gray-500">
                  {{ member.email }}
                </div>
              </div>
            </div>
            <div>
              <button
                v-if="can('remove members') && !confirm"
                type="button"
                class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                @click="confirm=true"
              >
                Remove member
              </button>
            </div>
          </div>
        </div>

        <template v-if="confirm">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <!-- Heroicon name: outline/exclamation -->
              <svg
                class="h-6 w-6 text-red-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3
                id="modal-title"
                class="text-lg leading-6 font-medium text-gray-900"
              >
                Are you sure?
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  {{ member.name }} will be removed from {{ team.name }} permanently. To undo this you'll have to re-invite them.
                </p>
              </div>
            </div>
          </div>
          <!--Buttons-->
          <div class="flex justify-between">
            <button
              class="bg-danger text-white w-2/5 py-2 mx-2 rounded-lg"
              @click="removeTeamMember"
            >
              Remove {{ member.name }}
            </button>
            <button
              class="bg-gray-300  w-2/5 py-2 mx-2 rounded-lg"
              @click="confirm = false"
            >
              Cancel
            </button>
          </div>
        </template>

        <!-- member edit status -->
        <template v-else>
          <div
            v-if="availableStatuses.length > 0"
            class="col-span-6 lg:col-span-4"
          >
            <jet-label
              for="statuses"
              value="Status"
            />
            <div class="relative z-0 mt-1 border border-gray-200 rounded-lg cursor-pointer">
              <button
                v-for="(status, i) in availableStatuses"
                :key="status"
                type="button"
                class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                :class="{'border-t border-gray-200 rounded-t-none': i > 0, 'rounded-b-none': i !== Object.keys(availableStatuses).length - 1}"
                @click="form.status = status"
              >
                <div :class="{'opacity-50': form.status && form.status !== status}">
                  <!-- Role Name -->
                  <div class="flex items-center">
                    <div
                      class="text-sm text-gray-600 capitalize"
                      :class="{'font-semibold': form.status === status}"
                    >
                      {{ status }}
                    </div>
                    <svg
                      v-if="form.status === status"
                      class="ml-2 h-5 w-5 text-green-400"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    ><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- member edit Role -->
          <div
            v-if="availableRoles.length > 0"
            class="col-span-6 lg:col-span-4"
          >
            <jet-label
              for="roles"
              value="Role"
            />
            <div class="relative z-0 mt-1 border border-gray-200 rounded-lg cursor-pointer">
              <button
                v-for="(role, i) in availableRoles"
                :key="role"
                type="button"
                class="relative px-4 py-3 inline-flex w-full rounded-lg focus:z-10 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200"
                :class="{'border-t border-gray-200 rounded-t-none': i > 0, 'rounded-b-none': i !== Object.keys(availableRoles).length - 1}"
                @click="form.role = role"
              >
                <div :class="{'opacity-50': form.role && form.role !== role}">
                  <!-- Role Name -->
                  <div class="flex items-center">
                    <div
                      class="text-sm text-gray-600 capitalize"
                      :class="{'font-semibold': form.role === role}"
                    >
                      {{ role }}
                    </div>
                    <svg
                      v-if="form.role === role"
                      class="ml-2 h-5 w-5 text-green-400"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    ><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!--Buttons-->
          <div class="flex justify-between">
            <button
              class="bg-success text-white w-2/5 py-2 mx-2 rounded-lg"
              type="submit"
            >
              Save
            </button>
            <button
              class="bg-gray-300  w-2/5 py-2 mx-2 rounded-lg"
              @click="close()"
            >
              Cancel
            </button>
          </div>
        </template>
      </div>
    </form>
  </modal>
</template>

<script>
import Modal from '../../Jetstream/Modal.vue';
import JetLabel from '../../Jetstream/Label.vue';

export default {
  name: 'EditTeamMember',
  components: {
    Modal,
    JetLabel,
  },
  props: {
    availableRoles: {
      type: Array,
      required: true,
    },
    availableStatuses: {
      type: Array,
      required: true,
    },
    team: {
      type: Object,
      required: true,
    },
    member: {
      type: Object,
    },
  },
  emits: ['close'],
  data() {
    return {
      form: this.$inertia.form({
        status: null,
        role: null,
      }),
      confirm: false,
    };
  },
  computed: {
    show() {
      return this.member !== null;
    },
  },
  watch: {
    member(val) {
      if (val !== null) {
        this.form.status = val.membership.status;
        this.form.role = val.role.name;
      }
    },
  },
  methods: {
    editTeamMember() {
      this.form.put(route('team-members.update', { team: this.team, user: this.member }), {
        errorBag: 'editTeamMember',
        preserveScroll: true,
        onSuccess: () => {
          this.$notify({
            type: 'success',
            title: 'Updated',
            text: `${this.member.name} has been updated`,
          }, 4000);

          this.form.reset();
          this.close();
        },

        onError: () => {
          this.$notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not update the user',
          }, 4000);
        },
      });
    },
    removeTeamMember(e) {
      e.preventDefault();
      this.$inertia.delete(route('team-members.destroy', [this.team, this.member]), {
        onSuccess: () => {
          this.$notify({
            type: 'info',
            title: 'Removed',
            text: 'The user was removed from the team',
          }, 4000);
          this.close();
        },
        onError: () => {
          this.$notify({
            type: 'danger',
            title: 'Something went wrong',
            text: 'Could not remove the user',
          }, 4000);
          this.close();
        },
      });
    },
    close() {
      this.confirm = false;
      this.$emit('close');
    },
  },
};
</script>
